import { useTranslation } from 'react-i18next';
import { getToday, getThirtyDaysAgo } from '@anytag/shared-utils';
import { DownloadFileType, FormAction, Option, SelectedRangeOption } from '@anytag/shared-types';
import { Form, BaseModal, ModalBody, BaseModalProps, DownloadSubmenuIcon, ModalFooter } from '@anytag/shared-atoms';
import { RangeDatePickerField, SelectorField } from '../../rhf-fields';
import { ExportTagsFormKeys, ExportTagsFormValues, ExportTagsSchema } from './types';

export interface ExportTagsModalProps
  extends FormAction<ExportTagsFormValues>,
    Pick<BaseModalProps, 'open' | 'onClose' | 'title'> {
  downloading?: boolean;
}

export const ExportTagsModal = ({
  open,
  title,
  onClose,
  onSubmit,
  downloading,
  defaultValues
}: ExportTagsModalProps) => {
  const { t } = useTranslation();

  const fileOptions: Option[] = [
    { label: t<string>('CSV'), value: DownloadFileType.CSV, prefixIcon: <DownloadSubmenuIcon icon="csv" /> },
    { label: t<string>('Excel'), value: DownloadFileType.EXCEL, prefixIcon: <DownloadSubmenuIcon icon="excel" /> },
    {
      label: t<string>('Spreadsheet'),
      value: DownloadFileType.SPREADSHEET,
      prefixIcon: <DownloadSubmenuIcon icon="spreadsheet" />
    }
  ];

  return (
    <BaseModal open={open} onClose={onClose} title={title ?? t('HashTag Export')} asChild>
      <Form.Provider<ExportTagsFormValues>
        onSubmit={onSubmit}
        zodSchema={ExportTagsSchema}
        defaultValues={{
          type: DownloadFileType.CSV,
          endDate: defaultValues?.endDate ?? getToday(),
          startDate: defaultValues?.startDate ?? getThirtyDaysAgo()
        }}
      >
        <ModalBody>
          <RangeDatePickerField<ExportTagsFormKeys>
            clearable={false}
            withinPortal={false}
            title={t('Selector.Date Range')}
            disabledRange={{ after: new Date() }}
            name={{ startDate: 'startDate', endDate: 'endDate' }}
            rangeList={[
              SelectedRangeOption.TODAY,
              SelectedRangeOption.LAST_SEVEN_DAYS,
              SelectedRangeOption.TODAY_TO_MONTH_AGO,
              SelectedRangeOption.THIS_MONTH,
              SelectedRangeOption.LAST_MONTH,
              SelectedRangeOption.CUSTOM
            ]}
          />

          <SelectorField<ExportTagsFormKeys>
            name="type"
            clearable={false}
            options={fileOptions}
            title={t('Selector.File Type')}
          />
        </ModalBody>

        <ModalFooter
          cancelButton={{ onClick: onClose }}
          submitButton={{ type: 'submit', loading: downloading, title: t('Button.Export') }}
        />
      </Form.Provider>
    </BaseModal>
  );
};
